import {
  Dispatch,
  SetStateAction,
  startTransition,
  useEffect,
  useMemo,
  useState,
} from "react";
import { UserLoggedAtom } from "@models/state/atoms/user-logged.atom";
import FollowButton from "@components/molecules/follow-button";
import useAmplitude from "@models/interactions/use-amplitude";
import { WriterInterface } from "@carglassgithub/memod-sdk";
import { useAtomValue, useStore } from "jotai";
import Avatar from "@components/atoms/avatar";
import { UserAtom } from "@models/state";
import classNames from "classnames";
import { sdk, sdkv1 } from "@models/api";
import Link from "next/link";
import {
  AUTH_STATUS,
  AuthModalAtom,
} from "@models/state/atoms/auth-modal.atom";
import Cookies from "cookie-universal";

interface Props {
  className?: string;
  writer: WriterInterface;
  size?: "default" | "small";
  showFollow?: boolean;
  showBlock?: boolean;
  onSave?: Dispatch<SetStateAction<WriterInterface[]>>;
}

const cookies = Cookies();

export function useAuthor(props: Props) {
  const { writer, size = "default" } = props;
  const { operations } = useAmplitude();
  const store = useStore();
  const logged = useAtomValue(UserLoggedAtom);

  const name = useMemo(
    () => `${writer.firstname?.trim() ?? ""} ${writer.lastname?.trim() ?? ""}`,
    [writer],
  );

  const username = useMemo(
    () =>
      writer.displayname?.trim().length > 0 ? `@${writer?.displayname}` : "",
    [writer],
  );

  const imageSize = useMemo(() => (size === "default" ? 40 : 38), [size]);

  const classname = classNames(
    "gap-2.5 xl:gap-0 items-start justify-start xl:justify-between",
    props.className,
    {
      "hidden lg:flex": !props.className,
    },
  );

  async function blockUser() {
    const token = cookies.get("token");

    await sdk.http.http.post(
      `/block-users/${props.writer?.id}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token || ""}`,
          "content-type": "text/json",
        },
      },
    );

    const { data: block_users } = await sdk.http.http.get("block-users", {
      headers: {
        Authorization: `Bearer ${token || ""}`,
        "content-type": "text/json",
      },
    });

    props.onSave?.(block_users);
  }

  //@ts-ignore add this to the method
  const isBlocked = props.writer?.is_blocked;

  const user = useAtomValue(UserAtom);

  const [follow, setFollow] = useState(false);

  const handleRedirect = () => {
    // if the displayname is missing using the id;
    const search = writer.displayname.trim().length > 0
      ? writer.displayname
      : writer.id;
    window.location.href = `/${search}/memos`;
  };

  useEffect(() => {
    startTransition(() =>
      setFollow(props.writer.is_following === 1 && logged!)
    );

    return () => setFollow(false);
  }, [logged, props.writer?.id, props.writer?.is_following]);

  const handleFollow = () => {
    if (logged) {
      sdkv1.author.follow(props.writer?.id).then(() =>
        setFollow((state) => {
          operations.send(!state ? "Followed Entity" : "Unfollowed Entity", {
            entity_type: "user",
            [!state ? "followed_at" : "unfollowed_at"]: Date.now(),
            entity_name: props.writer?.displayname,
            entity_id: props.writer?.id,
          });

          return !state;
        })
      );
    } else {
      store.set(AuthModalAtom, {
        open: true,
        mode: AUTH_STATUS.SIGN_UP,
      });
    }
  };

  const showFollow = user?.id! !== props.writer?.id && user?.id;

  return {
    model: {
      name,
      username,
      writer,
      size,
      imageSize,
      classname,
      isFollowing: follow,
      displayname: writer?.displayname,
      showFollow,
      isBlocked,
    },
    operations: {
      handleRedirect,
      handleFollow,
      blockUser,
    },
  };
}

export default function ProfileOptions(props: Props) {
  const { model, operations } = useAuthor(props);

  return (
    <section className="flex h-[166px] w-[266px] flex-col justify-between gap-[9px] rounded-lg bg-[#1C1C1E] p-3">
      <Link
        href={`/${model.displayname}/memos`}
        className="flex max-w-[171px] cursor-pointer items-center gap-2"
        onClick={operations.handleRedirect}
      >
        <Avatar src={props.writer?.photo?.url} size={model.imageSize} />
        <div>
          <p className="font-semibold leading-6 text-white line-clamp-1">
            {model.name}
          </p>
          <p className="text-sm leading-5 text-memod-label line-clamp-1">
            {model.username}
          </p>
        </div>
      </Link>

      <article className="text-xs text-memod-label line-clamp-3">
        {props.writer.description}
      </article>

      <footer className="flex h-8 items-center justify-between">
        <span className="flex gap-1 text-[#EBEBF5]/60">
          <p className="text-[#EBEBF5]">{props.writer.total_memos}</p>
          Memos
        </span>
        <FollowButton
          onClick={operations.handleFollow}
          isFollow={model.isFollowing}
          size={"small"}
          className="!h-8"
        />
      </footer>
    </section>
  );
}
